import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconCalculator,
  IconCalendar,
  IconClock,
  IconPrinter,
  IconDownload,
  IconBuildingStore,
  IconBox,
  IconCurrencyDollar,
  IconFileText,
  IconListDetails,
  IconScale,
  IconBuildingBank,
  IconPlus,
  IconSearch,
  IconChevronDown,
  IconChevronRight,
  IconPuzzle,
  IconToolsKitchen2,
  IconLayersLinked,
} from "@tabler/icons-react";
import { useItemizedProfitLoss } from "../controllers/reports.controller";
import { useTheme } from "../contexts/ThemeContext";

export default function ProfitLossReportPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substring(0, 10);
  const today = now.toISOString().substring(0, 10);

  const [filterType, setFilterType] = useState("this_month");
  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [activeTab, setActiveTab] = useState("audit"); // "audit" or "statement"
  const [marginViewMode, setMarginViewMode] = useState("grouped"); // "grouped", "separate", "addons_only"
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpand = (idx) => {
    setExpandedItems((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const { data, isLoading, error } = useItemizedProfitLoss({
    type: filterType,
    from: dateFrom,
    to: dateTo,
    fromTime,
    toTime,
  });

  const handlePresetChange = (preset) => {
    setFilterType(preset);
    const curr = new Date();
    if (preset === "today") {
      const d = curr.toISOString().substring(0, 10);
      setDateFrom(d);
      setDateTo(d);
    } else if (preset === "yesterday") {
      const y = new Date(curr);
      y.setDate(y.getDate() - 1);
      const d = y.toISOString().substring(0, 10);
      setDateFrom(d);
      setDateTo(d);
    } else if (preset === "last_7days") {
      const l7 = new Date(curr);
      l7.setDate(l7.getDate() - 7);
      setDateFrom(l7.toISOString().substring(0, 10));
      setDateTo(curr.toISOString().substring(0, 10));
    } else if (preset === "this_month") {
      setDateFrom(firstDay);
      setDateTo(today);
    } else if (preset === "last_month") {
      const lmFirst = new Date(curr.getFullYear(), curr.getMonth() - 1, 1).toISOString().substring(0, 10);
      const lmLast = new Date(curr.getFullYear(), curr.getMonth(), 0).toISOString().substring(0, 10);
      setDateFrom(lmFirst);
      setDateTo(lmLast);
    } else if (preset === "all_time") {
      setDateFrom("2020-01-01");
      setDateTo(today);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = async () => {
    if (!data?.marginAnalysis) return;
    try {
      const { Parser } = await import("@json2csv/plainjs");
      const { saveAs } = await import("file-saver");

      let rows = [];

      if (marginViewMode === "addons_only") {
        rows = (data.addonsAnalysis || []).map((ad) => ({
          "Add-on Name": ad.title,
          "Parent Menu Item": ad.parentItemTitle,
          "Qty Sold": ad.quantity,
          "Unit Price": `${currency} ${ad.unitPrice.toFixed(2)}`,
          "Net Sales": `${currency} ${ad.netSales.toFixed(2)}`,
          "Total Inv. Cost": `${currency} ${ad.totalInvCost.toFixed(2)}`,
          "Contribution": `${currency} ${ad.contribution.toFixed(2)}`,
          "Margin (%)": `${ad.contributionMarginPct}%`,
          "Recipe Ingredients": (ad.ingredients || [])
            .map((ing) => `${ing.ingredientTitle} (x${ing.totalQuantityUsed.toFixed(2)} ${ing.unit}): ${currency} ${ing.totalCost.toFixed(2)}`)
            .join(" | "),
        }));
      } else if (marginViewMode === "separate") {
        data.marginAnalysis.forEach((item) => {
          rows.push({
            "Item / Add-on": `${item.title} (Base)`,
            "Type": "Menu Item",
            "Category": item.category,
            "Qty Sold": item.quantity,
            "Total Inv. Cost": item.baseInvCost.toFixed(2),
            "Net Sales": item.baseNetSales.toFixed(2),
            "Contribution": (item.baseNetSales - item.baseInvCost).toFixed(2),
            "Margin (%)": item.baseNetSales > 0 ? (((item.baseNetSales - item.baseInvCost) / item.baseNetSales) * 100).toFixed(1) : "0.0",
            "Recipe Ingredients": (item.baseIngredients || [])
              .map((ing) => `${ing.ingredientTitle} (x${ing.totalQuantityUsed.toFixed(2)} ${ing.unit}): ${currency} ${ing.totalCost.toFixed(2)}`)
              .join(" | "),
          });

          (item.addons || []).forEach((ad) => {
            rows.push({
              "Item / Add-on": `[Add-on] ${ad.title} (for ${item.menuItemTitle})`,
              "Type": "Add-on",
              "Category": item.category,
              "Qty Sold": ad.quantity,
              "Total Inv. Cost": ad.totalInvCost.toFixed(2),
              "Net Sales": ad.netSales.toFixed(2),
              "Contribution": ad.contribution.toFixed(2),
              "Margin (%)": ad.contributionMarginPct,
              "Recipe Ingredients": (ad.ingredients || [])
                .map((ing) => `${ing.ingredientTitle} (x${ing.totalQuantityUsed.toFixed(2)} ${ing.unit}): ${currency} ${ing.totalCost.toFixed(2)}`)
                .join(" | "),
            });
          });
        });
      } else {
        rows = data.marginAnalysis.map((item) => ({
          "Menu Item": item.title,
          "Category": item.category,
          "Qty Sold": item.quantity,
          "Base Net Sales": item.baseNetSales?.toFixed(2) || item.netSales.toFixed(2),
          "Add-on Net Sales": item.addonNetSales?.toFixed(2) || "0.00",
          "Total Net Sales": item.netSales.toFixed(2),
          "Base Inv. Cost": item.baseInvCost?.toFixed(2) || item.totalInvCost.toFixed(2),
          "Add-on Inv. Cost": item.addonInvCost?.toFixed(2) || "0.00",
          "Total Inv. Cost": item.totalInvCost.toFixed(2),
          "Contribution": item.contribution.toFixed(2),
          "Margin (%)": item.contributionMarginPct,
          "Add-ons Ordered": (item.addons || [])
            .map((ad) => `${ad.title} (x${ad.quantity} @ ${currency} ${ad.unitPrice.toFixed(2)})`)
            .join(" | "),
          "Recipe Ingredients": (item.ingredients || [])
            .map((ing) => `${ing.isAddon ? `[Addon: ${ing.addonTitle}] ` : ""}${ing.ingredientTitle} (x${ing.totalQuantityUsed.toFixed(2)} ${ing.unit}): ${currency} ${ing.totalCost.toFixed(2)}`)
            .join(" | "),
        }));
      }

      // Add Summary Row
      rows.push({
        "Menu Item": "TOTALS",
        "Category": "",
        "Qty Sold": "",
        "Total Inv. Cost": data.totals.totalInvCost.toFixed(2),
        "Net Sales": data.totals.totalNetSales.toFixed(2),
        "Contribution": data.totals.totalContribution.toFixed(2),
        "Margin (%)": data.totals.overallMarginPct,
        "Recipe Ingredients": "",
      });

      const parser = new Parser();
      const csv = parser.parse(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `Itemized_Profit_Loss_${marginViewMode}_${dateFrom}_${dateTo}.csv`);
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-96">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="p-8 text-center text-red-500 font-bold">
          Failed to load Profit & Loss report: {error.message}
        </div>
      </Page>
    );
  }

  const store = data?.store || { name: "Restaurant", currency: "NPR" };
  const currency = store.currency || "NPR";
  const summary = data?.summary || {
    grossRevenue: 0,
    inventoryCostCOGS: 0,
    operatingExpenses: 0,
    generalOperatingExpenses: 0,
    staffPayroll: 0,
    netProfit: 0,
    grossOperatingProfit: 0,
    netBusinessProfit: 0,
  };
  const marginAnalysis = data?.marginAnalysis || [];
  const addonsAnalysis = data?.addonsAnalysis || [];
  const totals = data?.totals || { totalInvCost: 0, totalNetSales: 0, totalContribution: 0, overallMarginPct: "0.0" };

  return (
    <Page>
      <div className="max-w-7xl mx-auto space-y-6 pb-16 print:p-0 print:m-0 print:max-w-none">
        {/* Accounting Reports Suite Navigation */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-4 print:hidden">
          <Link
            to="/dashboard/reports/profit-loss"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-restro-green text-white shadow-md shadow-green-100 dark:shadow-none"
          >
            <IconCalculator size={17} />
            Profit & Loss Audit
          </Link>
          <Link
            to="/dashboard/reports/trial-balance"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconScale size={17} />
            Trial Balance
          </Link>
          <Link
            to="/dashboard/reports/balance-sheet"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconBuildingBank size={17} />
            Balance Sheet
          </Link>
          <Link
            to="/dashboard/reports/ledger"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconFileText size={17} />
            General Ledger
          </Link>
        </div>

        {/* Top Control Bar (Screen only) */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-restro-text dark:text-white flex items-center gap-2">
              <IconCalculator size={28} className="text-restro-green" />
              Profit & Loss Financial Center
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Institutional Itemized Audit and Financial Statement with granular recipe margin analysis
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* View Switcher */}
            <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl border border-restro-border-green text-xs font-bold">
              <button
                onClick={() => setActiveTab("audit")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition ${
                  activeTab === "audit"
                    ? "bg-white dark:bg-gray-700 text-restro-green shadow-sm"
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <IconListDetails size={16} />
                Itemized Audit
              </button>
              <button
                onClick={() => setActiveTab("statement")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition ${
                  activeTab === "statement"
                    ? "bg-white dark:bg-gray-700 text-restro-green shadow-sm"
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <IconFileText size={16} />
                Summary Statement
              </button>
            </div>

            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-restro-gray hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-restro-text rounded-2xl font-bold text-xs md:text-sm transition active:scale-95"
            >
              <IconPrinter size={18} />
              Print Audit
            </button>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-restro-green hover:bg-restro-green-button-hover text-white rounded-2xl font-bold text-xs md:text-sm transition active:scale-95 shadow-md shadow-green-100 dark:shadow-none"
            >
              <IconDownload size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filter Bar (Screen only) */}
        <div className="bg-white dark:bg-gray-900 p-4 rounded-3xl border border-restro-border-green shadow-sm space-y-3 print:hidden">
          {/* Preset Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-black uppercase tracking-wider text-gray-400 mr-2">Filters:</span>
            {[
              { id: "today", label: "Today" },
              { id: "yesterday", label: "Yesterday" },
              { id: "last_7days", label: "Last 7 Days" },
              { id: "this_month", label: "This Month" },
              { id: "last_month", label: "Last Month" },
              { id: "all_time", label: "All Time" },
              { id: "custom", label: "Custom Range" },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => handlePresetChange(p.id)}
                className={`px-3 py-1.5 rounded-xl font-bold text-xs transition ${
                  filterType === p.id
                    ? "bg-restro-green text-white shadow-sm"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date & Time Selectors */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs md:text-sm">
            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
              <IconCalendar size={16} className="text-gray-400" />
              <span className="font-bold text-gray-500">From:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setFilterType("custom");
                  setDateFrom(e.target.value);
                }}
                className="bg-transparent font-bold outline-none"
              />
              <IconClock size={15} className="text-gray-400 ml-1" />
              <input
                type="time"
                value={fromTime}
                onChange={(e) => {
                  setFilterType("custom");
                  setFromTime(e.target.value);
                }}
                className="bg-transparent font-mono outline-none"
              />
            </div>

            <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
              <IconCalendar size={16} className="text-gray-400" />
              <span className="font-bold text-gray-500">To:</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setFilterType("custom");
                  setDateTo(e.target.value);
                }}
                className="bg-transparent font-bold outline-none"
              />
              <IconClock size={15} className="text-gray-400 ml-1" />
              <input
                type="time"
                value={toTime}
                onChange={(e) => {
                  setFilterType("custom");
                  setToTime(e.target.value);
                }}
                className="bg-transparent font-mono outline-none"
              />
            </div>
          </div>
        </div>

        {/* -------------------- AUDIT VIEW (MATCHING USER'S DOCUMENT) -------------------- */}
        {activeTab === "audit" && (
          <div id="printable-report" className="printable-report bg-white dark:bg-gray-900 border border-restro-border-green rounded-3xl p-6 md:p-10 shadow-sm print:border-none print:shadow-none print:p-0 print:m-0">
            {/* Professional White Paper Print Letterhead (Print Only) */}
            <div className="hidden print:block text-center pb-4 mb-6 border-b-2 border-black">
              <h1 className="text-2xl font-black uppercase tracking-wider text-black">
                {store.name}
              </h1>
              <p className="text-xs text-gray-700 mt-0.5">
                {[store.address, store.phone && `Phone: ${store.phone}`, store.panNo && `PAN/VAT: ${store.panNo}`].filter(Boolean).join(" • ")}
              </p>
              <div className="mt-3 pt-2 border-t border-gray-400 flex justify-between items-center text-[10pt] font-bold text-black">
                <span className="uppercase tracking-wide font-black">
                  ITEMIZED PROFIT & LOSS AUDIT STATEMENT
                </span>
                <span className="text-xs font-normal">
                  Period: <strong>{dateFrom} to {dateTo}</strong> ({fromTime} - {toTime})
                </span>
              </div>
              <div className="flex justify-between text-[9pt] text-gray-600 mt-1">
                <span>Printed On: {new Date().toLocaleString()}</span>
                <span>Currency: {currency}</span>
              </div>
            </div>

            {/* Document Header (Screen Only) */}
            <div className="text-center pb-8 border-b border-gray-200 dark:border-gray-800 print:hidden">
              <div className="inline-flex items-center justify-center gap-2 mb-2">
                <IconBuildingStore size={28} className="text-restro-green" />
                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-wider text-gray-900 dark:text-white">
                  {store.name}
                </h2>
              </div>
              <h3 className="text-xs md:text-sm font-black uppercase tracking-[0.25em] text-restro-green mb-1">
                ITEMIZED PROFIT & LOSS AUDIT
              </h3>
              <p className="text-xs font-semibold text-gray-500">
                Period: <span className="font-bold text-gray-700 dark:text-gray-300">{dateFrom} to {dateTo}</span>{" "}
                <span className="font-mono text-[11px] bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full ml-1">
                  {fromTime} - {toTime}
                </span>
              </p>
            </div>

            {/* Print KPI Summary Bar (Clean Black & White Paper) */}
            <div className="hidden print:grid grid-cols-4 gap-2 py-3 mb-6 border border-black text-center text-xs avoid-break">
              <div className="border-r border-black p-2">
                <div className="text-[9px] uppercase font-bold text-gray-600">Gross Revenue</div>
                <div className="text-sm font-black font-mono">{currency} {summary.grossRevenue.toFixed(2)}</div>
              </div>
              <div className="border-r border-black p-2">
                <div className="text-[9px] uppercase font-bold text-gray-600">Inventory Cost (COGS)</div>
                <div className="text-sm font-black font-mono">{currency} {summary.inventoryCostCOGS.toFixed(2)}</div>
              </div>
              <div className="border-r border-black p-2">
                <div className="text-[9px] uppercase font-bold text-gray-600">Operating Expenses</div>
                <div className="text-sm font-black font-mono">{currency} {summary.operatingExpenses.toFixed(2)}</div>
              </div>
              <div className="p-2">
                <div className="text-[9px] uppercase font-bold text-gray-600">Net Business Profit</div>
                <div className="text-sm font-black font-mono">{currency} {summary.netBusinessProfit.toFixed(2)}</div>
              </div>
            </div>

            {/* Top 4 KPI Summary Cards (Screen Only) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 py-8 border-b border-gray-200 dark:border-gray-800 print:hidden">
              {/* Gross Revenue */}
              <div className="p-4 bg-gray-50/70 dark:bg-gray-800/40 rounded-2xl border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between text-gray-400 mb-1">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-wider">GROSS REVENUE</span>
                  <IconArrowUpRight size={16} className="text-emerald-500" />
                </div>
                <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white font-mono">
                  {currency}. {summary.grossRevenue.toFixed(2)}
                </div>
              </div>

              {/* Inventory Cost (COGS) */}
              <div className="p-4 bg-gray-50/70 dark:bg-gray-800/40 rounded-2xl border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between text-gray-400 mb-1">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-wider">INVENTORY COST (COGS)</span>
                  <IconBox size={16} className="text-amber-500" />
                </div>
                <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white font-mono">
                  {currency}. {summary.inventoryCostCOGS.toFixed(2)}
                </div>
              </div>

              {/* Operating Expenses */}
              <div className="p-4 bg-gray-50/70 dark:bg-gray-800/40 rounded-2xl border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between text-gray-400 mb-1">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-wider">OPERATING EXPENSES</span>
                  <IconArrowDownRight size={16} className="text-rose-500" />
                </div>
                <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white font-mono">
                  {currency}. {summary.operatingExpenses.toFixed(2)}
                </div>
              </div>

              {/* Net Profit */}
              <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-900">
                <div className="flex items-center justify-between text-emerald-600 mb-1">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-wider">NET PROFIT</span>
                  <IconCurrencyDollar size={18} className="text-emerald-600" />
                </div>
                <div className="text-lg md:text-xl font-black text-emerald-600 font-mono">
                  {currency}. {summary.netProfit.toFixed(2)}
                </div>
              </div>
            </div>

            {/* SECTION I. ITEMIZED MARGIN ANALYSIS */}
            <div className="py-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-restro-green rounded-full" />
                  <h4 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-200">
                    I. ITEMIZED MARGIN ANALYSIS & ADD-ONS BREAKDOWN
                  </h4>
                </div>

                {/* View Mode & Search Controls (Screen only) */}
                <div className="flex flex-wrap items-center gap-2 print:hidden">
                  {/* Search Filter */}
                  <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs">
                    <IconSearch size={15} className="text-gray-400 mr-2 shrink-0" />
                    <input
                      type="text"
                      placeholder="Search items or add-ons..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent outline-none w-36 md:w-48 text-gray-800 dark:text-gray-200"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600 ml-1 text-xs">
                        ×
                      </button>
                    )}
                  </div>

                  {/* View Switcher */}
                  <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold">
                    <button
                      onClick={() => setMarginViewMode("grouped")}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg transition ${
                        marginViewMode === "grouped"
                          ? "bg-white dark:bg-gray-700 text-restro-green shadow-sm"
                          : "text-gray-500 hover:text-gray-800 dark:hover:text-white"
                      }`}
                      title="Grouped by Menu Item with Add-on Details"
                    >
                      <IconLayersLinked size={14} />
                      Grouped
                    </button>
                    <button
                      onClick={() => setMarginViewMode("separate")}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg transition ${
                        marginViewMode === "separate"
                          ? "bg-white dark:bg-gray-700 text-restro-green shadow-sm"
                          : "text-gray-500 hover:text-gray-800 dark:hover:text-white"
                      }`}
                      title="Explode Add-ons as Separate Lines"
                    >
                      <IconPuzzle size={14} />
                      Separate Lines
                    </button>
                    <button
                      onClick={() => setMarginViewMode("addons_only")}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-lg transition ${
                        marginViewMode === "addons_only"
                          ? "bg-white dark:bg-gray-700 text-restro-green shadow-sm"
                          : "text-gray-500 hover:text-gray-800 dark:hover:text-white"
                      }`}
                      title="View Add-on Items Only"
                    >
                      <IconToolsKitchen2 size={14} />
                      Add-ons ({addonsAnalysis.length})
                    </button>
                  </div>
                </div>
              </div>

              {/* Add-ons Connected Summary Banner */}
              {summary.totalAddonNetSales > 0 && (
                <div className="mb-4 p-3 bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-900/60 rounded-2xl flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-bold">
                    <IconPuzzle size={18} className="text-amber-600 shrink-0" />
                    <span>
                      Add-ons Connected: {addonsAnalysis.reduce((acc, a) => acc + (a.quantity || 0), 0)} add-on items ordered across menu items
                    </span>
                  </div>
                  <div className="flex items-center gap-4 font-mono font-bold">
                    <span className="text-emerald-700 dark:text-emerald-400">
                      Add-on Sales: {currency}. {summary.totalAddonNetSales.toFixed(2)}
                    </span>
                    <span className="text-amber-700 dark:text-amber-400">
                      Add-on Recipe COGS: {currency}. {summary.totalAddonInvCost.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs md:text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 text-[10px] md:text-[11px] font-black uppercase tracking-wider text-gray-400">
                      <th className="py-3 px-3 w-1/4">
                        {marginViewMode === "addons_only" ? "ADD-ON ITEM & PARENT" : "MENU ITEM & ADD-ONS"}
                      </th>
                      <th className="py-3 px-2 text-center w-12">QTY</th>
                      <th className="py-3 px-4 w-5/12">INVENTORY USED (RECIPE DETAIL)</th>
                      <th className="py-3 px-3 text-right">TOTAL INV. COST</th>
                      <th className="py-3 px-3 text-right">NET SALES</th>
                      <th className="py-3 px-3 text-right">CONTRIBUTION</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60 font-medium">
                    {/* MODE 1: GROUPED BY MENU ITEM (WITH CONNECTED ADD-ONS) */}
                    {marginViewMode === "grouped" &&
                      marginAnalysis
                        .filter((item) => {
                          if (!searchQuery) return true;
                          const q = searchQuery.toLowerCase();
                          return (
                            item.title?.toLowerCase().includes(q) ||
                            item.category?.toLowerCase().includes(q) ||
                            item.addons?.some((a) => a.title?.toLowerCase().includes(q))
                          );
                        })
                        .map((item, idx) => {
                          const hasAddons = item.addons && item.addons.length > 0;
                          const isExpanded = !!expandedItems[idx];

                          return (
                            <React.Fragment key={idx}>
                              <tr className="align-top hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                                {/* Menu Item & Category & Addon Badges */}
                                <td className="py-4 px-3">
                                  <div className="font-bold text-gray-900 dark:text-gray-100 text-xs md:text-sm">
                                    {item.title}
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-0.5">
                                    {item.category}
                                  </div>

                                  {/* Addon Badges and Price Connection */}
                                  {hasAddons && (
                                    <div className="mt-2 space-y-1">
                                      <div className="flex flex-wrap gap-1">
                                        {item.addons.map((ad) => (
                                          <span
                                            key={ad.addonId}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                                          >
                                            <IconPuzzle size={11} /> {ad.title} (x{ad.quantity} @ {currency}. {ad.unitPrice.toFixed(2)})
                                          </span>
                                        ))}
                                      </div>
                                      <div className="text-[10px] text-gray-500 font-mono">
                                        Base: {currency}. {item.basePrice?.toFixed(2)} | Add-ons Total: {currency}. {item.addonNetSales?.toFixed(2)}
                                      </div>
                                      <button
                                        onClick={() => toggleExpand(idx)}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 hover:text-amber-700 hover:underline pt-0.5 print:hidden"
                                      >
                                        {isExpanded ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                                        {isExpanded ? "Hide Add-on Breakdown" : `Show Add-on Details (${item.addons.length})`}
                                      </button>
                                    </div>
                                  )}
                                </td>

                                {/* Qty Sold */}
                                <td className="py-4 px-2 text-center font-bold font-mono text-gray-700 dark:text-gray-300">
                                  {item.quantity}
                                </td>

                                {/* Recipe Ingredients Detail (Base + Add-ons clearly labeled) */}
                                <td className="py-4 px-4">
                                  {item.ingredients && item.ingredients.length > 0 ? (
                                    <div className="space-y-1 text-[11px]">
                                      {item.ingredients.map((ing, iIdx) => (
                                        <div
                                          key={iIdx}
                                          className={`flex justify-between items-center ${
                                            ing.isAddon
                                              ? "text-amber-700 dark:text-amber-300 font-medium"
                                              : "text-gray-600 dark:text-gray-400"
                                          }`}
                                        >
                                          <span className="flex items-center gap-1.5">
                                            {ing.isAddon ? (
                                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 font-black">
                                                Addon: {ing.addonTitle}
                                              </span>
                                            ) : (
                                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-bold">
                                                Base
                                              </span>
                                            )}
                                            <span>
                                              {ing.ingredientTitle}{" "}
                                              <span className="text-[10px] text-gray-400 font-mono">
                                                (x{ing.totalQuantityUsed.toFixed(2)} {ing.unit})
                                              </span>
                                            </span>
                                          </span>
                                          <span className="font-mono text-gray-800 dark:text-gray-200 font-medium ml-2">
                                            {currency}. {ing.totalCost.toFixed(2)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-gray-400 italic">No recipe linked</span>
                                  )}
                                </td>

                                {/* Total Inv Cost */}
                                <td className="py-4 px-3 text-right">
                                  <div className="font-mono font-bold text-amber-600 dark:text-amber-500">
                                    {currency}. {item.totalInvCost.toFixed(2)}
                                  </div>
                                  {hasAddons && (
                                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                                      Base: {item.baseInvCost?.toFixed(2)} + Addon: {item.addonInvCost?.toFixed(2)}
                                    </div>
                                  )}
                                </td>

                                {/* Net Sales */}
                                <td className="py-4 px-3 text-right">
                                  <div className="font-mono font-bold text-emerald-600 dark:text-emerald-500">
                                    {currency}. {item.netSales.toFixed(2)}
                                  </div>
                                  {hasAddons && (
                                    <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                                      Base: {item.baseNetSales?.toFixed(2)} + Addon: {item.addonNetSales?.toFixed(2)}
                                    </div>
                                  )}
                                </td>

                                {/* Contribution */}
                                <td className="py-4 px-3 text-right">
                                  <div className="font-mono font-bold text-gray-900 dark:text-white">
                                    {currency}. {item.contribution.toFixed(2)}
                                  </div>
                                  <div className="text-[10px] text-emerald-600 font-semibold">
                                    {item.contributionMarginPct}%
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded Addon Detail Drawer */}
                              {hasAddons && isExpanded && (
                                <tr className="bg-amber-50/40 dark:bg-amber-950/20">
                                  <td colSpan={6} className="p-4 border-t border-b border-amber-200/50 dark:border-amber-800/40">
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-amber-200 dark:border-amber-800 shadow-sm space-y-3">
                                      <div className="flex items-center justify-between text-xs font-black text-amber-800 dark:text-amber-200 uppercase tracking-wider">
                                        <span className="flex items-center gap-1.5">
                                          <IconPuzzle size={16} className="text-amber-600" />
                                          Add-on Breakdown for {item.title}
                                        </span>
                                        <span>Total Add-on Revenue: {currency}. {item.addonNetSales?.toFixed(2)}</span>
                                      </div>

                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-[10px] uppercase">
                                            <th className="py-2 text-left">Add-on Item</th>
                                            <th className="py-2 text-center">Qty</th>
                                            <th className="py-2 text-right">Unit Price</th>
                                            <th className="py-2 text-left pl-4">Recipe Ingredients Used</th>
                                            <th className="py-2 text-right">Add-on COGS</th>
                                            <th className="py-2 text-right">Net Sales</th>
                                            <th className="py-2 text-right">Contribution</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                          {item.addons.map((ad) => (
                                            <tr key={ad.addonId}>
                                              <td className="py-2 font-bold text-gray-800 dark:text-gray-200">
                                                {ad.title}
                                              </td>
                                              <td className="py-2 text-center font-mono font-bold">
                                                {ad.quantity}
                                              </td>
                                              <td className="py-2 text-right font-mono text-gray-600 dark:text-gray-300">
                                                {currency}. {ad.unitPrice.toFixed(2)}
                                              </td>
                                              <td className="py-2 pl-4 text-gray-600 dark:text-gray-400 text-[11px]">
                                                {ad.ingredients && ad.ingredients.length > 0 ? (
                                                  ad.ingredients.map((ing, iIdx) => (
                                                    <span key={iIdx} className="mr-2">
                                                      {ing.ingredientTitle} (x{ing.totalQuantityUsed.toFixed(2)} {ing.unit}): {currency}. {ing.totalCost.toFixed(2)}
                                                    </span>
                                                  ))
                                                ) : (
                                                  <span className="italic text-gray-400">No recipe linked</span>
                                                )}
                                              </td>
                                              <td className="py-2 text-right font-mono font-bold text-amber-600">
                                                {currency}. {ad.totalInvCost.toFixed(2)}
                                              </td>
                                              <td className="py-2 text-right font-mono font-bold text-emerald-600">
                                                {currency}. {ad.netSales.toFixed(2)}
                                              </td>
                                              <td className="py-2 text-right font-mono font-bold text-gray-900 dark:text-white">
                                                {currency}. {ad.contribution.toFixed(2)} ({ad.contributionMarginPct}%)
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                    {/* MODE 2: SEPARATE ADD-ON LINES */}
                    {marginViewMode === "separate" &&
                      marginAnalysis.flatMap((item, idx) => {
                        const baseRows = [];
                        const q = searchQuery.toLowerCase();

                        // Base item row
                        if (!searchQuery || item.title?.toLowerCase().includes(q) || item.category?.toLowerCase().includes(q)) {
                          baseRows.push(
                            <tr key={`base-${idx}`} className="align-top hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                              <td className="py-4 px-3">
                                <div className="font-bold text-gray-900 dark:text-gray-100 text-xs md:text-sm">
                                  {item.title} <span className="badge badge-ghost badge-xs">Base Item</span>
                                </div>
                                <div className="text-[10px] text-gray-400 mt-0.5">{item.category}</div>
                              </td>
                              <td className="py-4 px-2 text-center font-bold font-mono text-gray-700 dark:text-gray-300">
                                {item.quantity}
                              </td>
                              <td className="py-4 px-4">
                                {item.baseIngredients && item.baseIngredients.length > 0 ? (
                                  <div className="space-y-1 text-[11px]">
                                    {item.baseIngredients.map((ing, iIdx) => (
                                      <div key={iIdx} className="flex justify-between items-center text-gray-600 dark:text-gray-400">
                                        <span>
                                          {ing.ingredientTitle} <span className="text-[10px] text-gray-400 font-mono">(x{ing.totalQuantityUsed.toFixed(2)} {ing.unit})</span>
                                        </span>
                                        <span className="font-mono text-gray-800 dark:text-gray-300 font-medium ml-2">
                                          {currency}. {ing.totalCost.toFixed(2)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-gray-400 italic">No recipe linked</span>
                                )}
                              </td>
                              <td className="py-4 px-3 text-right font-mono font-bold text-amber-600 dark:text-amber-500">
                                {currency}. {item.baseInvCost?.toFixed(2) || "0.00"}
                              </td>
                              <td className="py-4 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-500">
                                {currency}. {item.baseNetSales?.toFixed(2) || item.netSales.toFixed(2)}
                              </td>
                              <td className="py-4 px-3 text-right">
                                <div className="font-mono font-bold text-gray-900 dark:text-white">
                                  {currency}. {((item.baseNetSales || item.netSales) - (item.baseInvCost || 0)).toFixed(2)}
                                </div>
                                <div className="text-[10px] text-emerald-600 font-semibold">
                                  {item.baseNetSales > 0 ? ((((item.baseNetSales - (item.baseInvCost || 0)) / item.baseNetSales) * 100).toFixed(1)) : "0.0"}%
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        // Addon rows
                        (item.addons || []).forEach((ad, adIdx) => {
                          if (!searchQuery || ad.title?.toLowerCase().includes(q) || item.title?.toLowerCase().includes(q)) {
                            baseRows.push(
                              <tr key={`ad-${idx}-${adIdx}`} className="align-top bg-amber-50/25 dark:bg-amber-950/10 hover:bg-amber-50/50 transition-colors">
                                <td className="py-3 px-3 pl-6">
                                  <div className="font-bold text-amber-800 dark:text-amber-300 text-xs md:text-sm flex items-center gap-1.5">
                                    <IconPuzzle size={13} className="text-amber-600" />
                                    [Add-on] {ad.title}
                                  </div>
                                  <div className="text-[10px] text-gray-400 mt-0.5">
                                    For: {item.menuItemTitle || item.title}
                                  </div>
                                </td>
                                <td className="py-3 px-2 text-center font-bold font-mono text-amber-700 dark:text-amber-300">
                                  {ad.quantity}
                                </td>
                                <td className="py-3 px-4">
                                  {ad.ingredients && ad.ingredients.length > 0 ? (
                                    <div className="space-y-1 text-[11px]">
                                      {ad.ingredients.map((ing, iIdx) => (
                                        <div key={iIdx} className="flex justify-between items-center text-amber-700 dark:text-amber-300 font-medium">
                                          <span>
                                            {ing.ingredientTitle} <span className="text-[10px] text-gray-400 font-mono">(x{ing.totalQuantityUsed.toFixed(2)} {ing.unit})</span>
                                          </span>
                                          <span className="font-mono ml-2">
                                            {currency}. {ing.totalCost.toFixed(2)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-gray-400 italic">No recipe linked</span>
                                  )}
                                </td>
                                <td className="py-3 px-3 text-right font-mono font-bold text-amber-600">
                                  {currency}. {ad.totalInvCost.toFixed(2)}
                                </td>
                                <td className="py-3 px-3 text-right font-mono font-bold text-emerald-600">
                                  {currency}. {ad.netSales.toFixed(2)}
                                </td>
                                <td className="py-3 px-3 text-right">
                                  <div className="font-mono font-bold text-gray-900 dark:text-white">
                                    {currency}. {ad.contribution.toFixed(2)}
                                  </div>
                                  <div className="text-[10px] text-emerald-600 font-semibold">
                                    {ad.contributionMarginPct}%
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        });

                        return baseRows;
                      })}

                    {/* MODE 3: ADD-ONS ONLY */}
                    {marginViewMode === "addons_only" &&
                      addonsAnalysis
                        .filter((ad) => {
                          if (!searchQuery) return true;
                          const q = searchQuery.toLowerCase();
                          return ad.title?.toLowerCase().includes(q) || ad.parentItemTitle?.toLowerCase().includes(q);
                        })
                        .map((ad, adIdx) => (
                          <tr key={adIdx} className="align-top hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                            <td className="py-4 px-3">
                              <div className="font-bold text-amber-800 dark:text-amber-300 text-xs md:text-sm flex items-center gap-1.5">
                                <IconPuzzle size={15} className="text-amber-600" />
                                {ad.title}
                              </div>
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                Parent: {ad.parentItemTitle} • Unit Price: {currency}. {ad.unitPrice.toFixed(2)}
                              </div>
                            </td>
                            <td className="py-4 px-2 text-center font-bold font-mono text-gray-700 dark:text-gray-300">
                              {ad.quantity}
                            </td>
                            <td className="py-4 px-4">
                              {ad.ingredients && ad.ingredients.length > 0 ? (
                                <div className="space-y-1 text-[11px]">
                                  {ad.ingredients.map((ing, iIdx) => (
                                    <div key={iIdx} className="flex justify-between items-center text-gray-600 dark:text-gray-400">
                                      <span>
                                        {ing.ingredientTitle} <span className="text-[10px] text-gray-400 font-mono">(x{ing.totalQuantityUsed.toFixed(2)} {ing.unit})</span>
                                      </span>
                                      <span className="font-mono text-gray-800 dark:text-gray-300 font-medium ml-2">
                                        {currency}. {ing.totalCost.toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-[11px] text-gray-400 italic">No recipe linked</span>
                              )}
                            </td>
                            <td className="py-4 px-3 text-right font-mono font-bold text-amber-600 dark:text-amber-500">
                              {currency}. {ad.totalInvCost.toFixed(2)}
                            </td>
                            <td className="py-4 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-500">
                              {currency}. {ad.netSales.toFixed(2)}
                            </td>
                            <td className="py-4 px-3 text-right">
                              <div className="font-mono font-bold text-gray-900 dark:text-white">
                                {currency}. {ad.contribution.toFixed(2)}
                              </div>
                              <div className="text-[10px] text-emerald-600 font-semibold">
                                {ad.contributionMarginPct}%
                              </div>
                            </td>
                          </tr>
                        ))}

                    {marginAnalysis.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-10 text-center text-gray-400">
                          No sales recorded in the selected period.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {/* TOTALS FOR PERIOD */}
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 font-black text-xs md:text-sm">
                      <td colSpan={3} className="py-4 px-3 uppercase tracking-wider text-gray-800 dark:text-gray-200">
                        {marginViewMode === "addons_only" ? "TOTALS FOR ADD-ONS" : "TOTALS FOR PERIOD"}
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-amber-600">
                        {currency}.{" "}
                        {marginViewMode === "addons_only"
                          ? addonsAnalysis.reduce((acc, a) => acc + (a.totalInvCost || 0), 0).toFixed(2)
                          : totals.totalInvCost.toFixed(2)}
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-emerald-600">
                        {currency}.{" "}
                        {marginViewMode === "addons_only"
                          ? addonsAnalysis.reduce((acc, a) => acc + (a.netSales || 0), 0).toFixed(2)
                          : totals.totalNetSales.toFixed(2)}
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-emerald-600">
                        {currency}.{" "}
                        {marginViewMode === "addons_only"
                          ? addonsAnalysis.reduce((acc, a) => acc + (a.contribution || 0), 0).toFixed(2)
                          : totals.totalContribution.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* SECTION II. OPERATING PROFIT RECONCILIATION */}
            <div className="py-8 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-4 bg-restro-green rounded-full" />
                <h4 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-200">
                  II. OPERATING PROFIT RECONCILIATION
                </h4>
              </div>

              <div className="max-w-xl mx-auto md:mx-0 bg-gray-50/80 dark:bg-gray-800/50 rounded-3xl p-6 border border-gray-200 dark:border-gray-800 space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-bold text-gray-700 dark:text-gray-300">Gross Operating Profit:</span>
                  <span className="font-mono font-bold text-emerald-600">
                    +{currency}. {summary.grossOperatingProfit.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>General Operating Expenses:</span>
                  <span className="font-mono text-rose-500">
                    -{currency}. {summary.generalOperatingExpenses.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>Staff Payroll & Benefits:</span>
                  <span className="font-mono text-rose-500">
                    -{currency}. {summary.staffPayroll.toFixed(2)}
                  </span>
                </div>

                <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <span className="text-sm md:text-base font-black uppercase tracking-wider text-gray-900 dark:text-white">
                    NET BUSINESS PROFIT
                  </span>
                  <span className="text-xl md:text-2xl font-black font-mono text-emerald-600">
                    {currency}. {summary.netBusinessProfit.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -------------------- STATEMENT SUMMARY VIEW (TAB 2) -------------------- */}
        {activeTab === "statement" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Revenue Column */}
            <div className="space-y-6">
              <div className="p-6 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-3xl shadow-sm">
                <div className="flex items-center gap-2 text-green-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <IconArrowUpRight size={18} /> Revenue
                </div>
                <h2 className="text-4xl font-bold text-green-600 font-mono">
                  {summary.grossRevenue.toFixed(2)}
                </h2>
              </div>

              <div className="border border-restro-border-green rounded-3xl overflow-hidden bg-white dark:bg-gray-900">
                <div className="bg-restro-bg-gray dark:bg-gray-800 p-4 font-bold border-b border-restro-border-green">
                  Revenue Breakdown
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span>Gross Sales Revenue</span>
                    <span className="font-mono font-medium">{summary.grossRevenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400 text-sm">
                    <span>Other Incomes</span>
                    <span className="font-mono">0.00</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Expense Column */}
            <div className="space-y-6">
              <div className="p-6 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-3xl shadow-sm">
                <div className="flex items-center gap-2 text-red-600 mb-2 font-bold uppercase tracking-wider text-xs">
                  <IconArrowDownRight size={18} /> Expenses
                </div>
                <h2 className="text-4xl font-bold text-red-600 font-mono">
                  {(summary.inventoryCostCOGS + summary.operatingExpenses).toFixed(2)}
                </h2>
              </div>

              <div className="border border-restro-border-green rounded-3xl overflow-hidden bg-white dark:bg-gray-900 text-sm">
                <div className="bg-restro-bg-gray dark:bg-gray-800 p-4 font-bold border-b border-restro-border-green">
                  Expense Breakdown
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex justify-between font-medium">
                    <span>Cost of Goods Sold (Recipe COGS)</span>
                    <span className="font-mono text-amber-600">{summary.inventoryCostCOGS.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>General Operating Expenses</span>
                    <span className="font-mono text-rose-500">{summary.generalOperatingExpenses.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Staff Payroll & Benefits</span>
                    <span className="font-mono text-rose-500">{summary.staffPayroll.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-base">
                    <span>Total Operating Costs</span>
                    <span className="font-mono text-red-600">
                      {(summary.inventoryCostCOGS + summary.operatingExpenses).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Profit Summary */}
            <div className="lg:col-span-1">
              <div
                className={`p-8 rounded-3xl border-2 flex flex-col items-center justify-center h-full text-center ${
                  summary.netBusinessProfit >= 0
                    ? "bg-restro-green border-restro-green-button-hover text-white shadow-xl"
                    : "bg-red-500 border-red-600 text-white shadow-xl"
                }`}
              >
                <IconCalculator size={48} stroke={1.5} className="mb-4 opacity-50" />
                <p className="uppercase font-bold tracking-[0.2em] text-sm opacity-80 mb-2">Net Business Profit</p>
                <h1 className="text-5xl md:text-6xl font-black mb-4 font-mono">
                  {summary.netBusinessProfit >= 0 ? "+" : ""}
                  {summary.netBusinessProfit.toFixed(2)}
                </h1>
                <p className="text-xs md:text-sm opacity-90 max-w-[240px]">
                  {summary.netBusinessProfit >= 0
                    ? "Excellent! Your business is generating positive operating returns for this period."
                    : "Your total operating costs exceed net revenue for this period."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formal Three-Column Signatures (Prepared By, Verified By, Approved By) */}
        <div className="signature-section mt-16 pt-8 border-t-2 border-black avoid-break">
          <div className="grid grid-cols-3 gap-6 text-center">
            {/* Prepared By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Prepared By</p>
              <p className="text-[11px] text-gray-500">Accountant / Cashier</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Verified By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Verified By</p>
              <p className="text-[11px] text-gray-500">Internal Auditor / Finance</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Approved By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Approved By</p>
              <p className="text-[11px] text-gray-500">Managing Director / Owner</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>
          </div>
        </div>
      </div>

      {/* Professional White Paper Print CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 15mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            font-size: 10pt !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, aside, header, .appbar, .navbar, .print\\:hidden, button, input, select, a {
            display: none !important;
          }
          div[class*="pl-"], div[class*="md:pl-"], [class*="pl-"] {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          body * {
            visibility: hidden;
          }
          #printable-report, #printable-report * {
            visibility: visible;
          }
          #printable-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
          #printable-report table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8.5pt !important;
            color: #000000 !important;
            background: #ffffff !important;
          }
          #printable-report th {
            background-color: #f3f4f6 !important;
            color: #000000 !important;
            border: 1px solid #9ca3af !important;
            padding: 5px 6px !important;
            font-weight: bold !important;
            text-transform: uppercase !important;
          }
          #printable-report td {
            border: 1px solid #d1d5db !important;
            padding: 4px 6px !important;
            color: #000000 !important;
            background: transparent !important;
          }
          #printable-report tfoot td {
            border-top: 2px solid #000000 !important;
            border-bottom: 3px double #000000 !important;
            font-weight: 800 !important;
            background-color: #f9fafb !important;
          }
          tr, .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .signature-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-top: 40px !important;
            padding-top: 15px !important;
          }
        }
      `}</style>
    </Page>
  );
}
